import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient, extractApiError } from '../api/axios';
import { API } from '../constants/api';

const initialState = {
  banners: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  loading: false,
  error: null,
};

export const fetchBanners = createAsyncThunk('banners/fetchBanners', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.products.banners);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error) || 'Failed to fetch banners');
  }
});

export const createBanner = createAsyncThunk('banners/createBanner', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.products.banners, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error) || 'Failed to create banner');
  }
});

export const updateBanner = createAsyncThunk('banners/updateBanner', async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await apiClient.put(API.products.bannerById(id), payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error) || 'Failed to update banner');
  }
});

export const deleteBanner = createAsyncThunk('banners/deleteBanner', async (id, { rejectWithValue }) => {
  try {
    const response = await apiClient.del(API.products.bannerById(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error) || 'Failed to delete banner');
  }
});

const bannerSlice = createSlice({
  name: 'banners',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload || { count: 0, next: null, previous: null, results: [] };
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBanner.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.loading = false;
        // A DELETE returns an empty body, so filter by the deleted id from
        // meta.arg — action.payload is undefined and matched nothing.
        state.banners.results = state.banners.results.filter(
          (banner) => banner.id !== action.meta.arg
        );
        state.banners.count -= 1;
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.banners.results = state.banners.results.map((banner) =>
            banner.id === action.payload.id ? action.payload : banner
          );
        }
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default bannerSlice.reducer;
